import React, { useState } from "react";
import {
  Button,
  Grid,
  List,
  ListItem, 
  Typography,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom"; 
import { StyledButton } from "../../../assets/css/styledComponent";
import { rootClientPath } from "../../../util/link";
import { BookingType } from "../../../types";
import {
  BookingTypeTitle,
  Circle,
} from "../../../pages/business/userPage/userBookingTypePage.styles";

interface BookingTypeListProps {
  bookingTypes: BookingType[];
  category: number;
  maxBookingTypes?: number;
  primaryColor?: string;
  embeded?: boolean;
  showBookTypeColor?: boolean;
  ignoreDisplayOnBookingPageflag?: boolean;
  getBookingPath: (bookingType: BookingType) => string;
}

const BookingTypeListTemplate: React.FC<BookingTypeListProps> = ({
  bookingTypes,
  category,
  maxBookingTypes = 3,
  primaryColor,
  embeded = false,
  showBookTypeColor = false,
  ignoreDisplayOnBookingPageflag = false,
  getBookingPath,
}) => {
  const navigate = useNavigate();
  const [showAllBookingTypes, setShowAllBookingTypes] =
    useState<boolean>(false);

  const handleBookButtonClick = (bookingType: BookingType) => {
    const path = getBookingPath(bookingType);
    embeded
      ? window.open(`${rootClientPath}${path}`, "_blank")
      : navigate(path);
  };

  const handleViewAllClick = () => setShowAllBookingTypes(true);
  const handleCollapseClick = () => setShowAllBookingTypes(false);

  // Filter and categorize booking types based on category and visibility (isPublic)
  const filteredBookingTypes = bookingTypes
    .filter(
      (bookingType) =>
        (ignoreDisplayOnBookingPageflag || bookingType.displayOnBookingPage) &&
        bookingType.bookingTypeCategoryId === category
    )
    .slice(0, showAllBookingTypes ? undefined : maxBookingTypes);

  const showViewMoreButton =
    filteredBookingTypes.length > maxBookingTypes && !showAllBookingTypes;

  return (
    <List>
      {filteredBookingTypes.map((bookingType) => (
        <React.Fragment key={bookingType.title}>
          <ListItem>
            <Grid
              container
              alignItems="center"
              wrap="nowrap"
              sx={{
                gap: { xs: 2, sm: 4, md: 8, lg: 12 },
                maxWidth: "100%",
              }}
            >
              {/* Booking Type Info */}
              <Grid
                container
                item
                xs
                sx={{
                  flexGrow: 1,
                  minWidth: "200px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {showBookTypeColor && (
                  <Circle style={{ background: bookingType.color }} />
                )}
                <BookingTypeTitle>{bookingType.title}</BookingTypeTitle>
              </Grid>

              {/* Price, Duration, and Button */}
              <Grid
                item
                container
                xs="auto"
                alignItems="center"
                justifyContent="flex-end"
                wrap="nowrap"
                sx={{ flexShrink: 0, gap: 2, maxWidth: "300px" }}
              >
                <Grid item sx={{ textAlign: "right", minWidth: "80px" }}>
                  <Typography>
                    {bookingType.price ? `$${bookingType.price}` : "Free"}
                  </Typography>
                  <Typography noWrap>{`${bookingType.length} mins`}</Typography>
                </Grid>
                <Grid item>
                  <StyledButton
                    style={{ backgroundColor: primaryColor }}
                    onClick={() => handleBookButtonClick(bookingType)}
                  >
                    Book
                  </StyledButton>
                </Grid>
              </Grid>
            </Grid>
          </ListItem>

          <Divider />
        </React.Fragment>
      ))}

      {/* View More / Collapse Buttons */}
      {showViewMoreButton && (
        <ListItem>
          <Button onClick={handleViewAllClick} variant="outlined">
            View all
          </Button>
        </ListItem>
      )}

      {showAllBookingTypes && (
        <ListItem>
          <Button onClick={handleCollapseClick} variant="outlined">
            Show less
          </Button>
        </ListItem>
      )}
    </List>
  );
};

export default BookingTypeListTemplate;
